import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MessageSquare, Send, ArrowLeft, Check, CheckCheck, Bell, BellRing, Paperclip, X, Loader2 } from "lucide-react";
import { enablePush, pushSupported, pushPermission } from "@/lib/push";
import { mediaUrl } from "@/lib/format";

const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
};
const initials = (name) => (name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("");

export default function Messages() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(params.get("to") || null);
  const [thread, setThread] = useState(null); // { other, messages }
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [notif, setNotif] = useState(pushPermission());
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const bottomRef = useRef(null);

  const loadThreads = () =>
    api.get("/messages/threads").then((r) => setThreads(r.data)).catch(() => {}).finally(() => setLoadingThreads(false));
  const loadThread = (id) =>
    api.get(`/messages/thread/${id}`).then((r) => setThread(r.data)).catch(() => toast.error("Couldn't open this conversation."));

  useEffect(() => { loadThreads(); }, []);
  useEffect(() => { const iv = setInterval(loadThreads, 15000); return () => clearInterval(iv); }, []);

  // Silently keep the push subscription fresh if the user already granted permission.
  useEffect(() => { if (pushSupported() && pushPermission() === "granted") enablePush({ interactive: false }).catch(() => {}); }, []);

  const turnOnNotifications = async () => {
    try { await enablePush({ interactive: true }); setNotif("granted"); toast.success("Message notifications are on."); }
    catch (e) { toast.error(e.message || "Couldn't enable notifications."); setNotif(pushPermission()); }
  };

  useEffect(() => {
    if (!activeId) { setThread(null); return; }
    loadThread(activeId);
    const iv = setInterval(() => loadThread(activeId), 8000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [activeId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread?.messages?.length]);

  const openThread = (id) => { setActiveId(id); setParams(id ? { to: id } : {}); setAttachment(null); };
  const backToList = () => { setActiveId(null); setThread(null); setParams({}); setAttachment(null); };

  const pickFile = async (e) => {
    const f = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Image must be 5 MB or smaller."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachment(data.url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't upload photo.");
    } finally { setUploading(false); }
  };

  const send = async (e) => {
    e?.preventDefault();
    const text = body.trim();
    if ((!text && !attachment) || !activeId || sending) return;
    setSending(true);
    try {
      await api.post(`/messages/thread/${activeId}`, { body: text, image_url: attachment || null });
      setBody("");
      setAttachment(null);
      await loadThread(activeId);
      loadThreads();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send message.");
    } finally { setSending(false); }
  };

  const otherLabel = user?.role === "subscriber" ? "customers" : "providers";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="messages-page">
      <Link to={user?.role === "subscriber" ? "/dashboard" : "/home"} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="back-to-dashboard">
        <ArrowLeft className="w-4 h-4" />Back to dashboard
      </Link>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter">Messages</h1>
          <p className="text-muted-foreground mt-2">Chat directly with the {otherLabel} you work with on BookdHUB.</p>
        </div>
        {pushSupported() && notif !== "unsupported" && (
          notif === "granted" ? (
            <span className="shrink-0 mt-2 inline-flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="notif-on">
              <BellRing className="w-4 h-4 text-primary" />Notifications on
            </span>
          ) : (
            <Button variant="outline" size="sm" className="shrink-0 mt-2 rounded-sm" onClick={turnOnNotifications} disabled={notif === "denied"} data-testid="notif-enable-btn">
              <Bell className="w-4 h-4 mr-1.5" />{notif === "denied" ? "Notifications blocked" : "Turn on notifications"}
            </Button>
          )
        )}
      </div>

      <div className="mt-8 grid md:grid-cols-[320px_1fr] gap-0 border border-border rounded-sm overflow-hidden bg-white min-h-[540px]">
        {/* Thread list */}
        <aside className={`border-r border-border ${activeId ? "hidden md:block" : "block"}`} data-testid="thread-list">
          <div className="px-4 py-3 border-b border-border">
            <span className="text-sm font-semibold">Conversations</span>
          </div>
          {loadingThreads ? (
            <p className="p-4 text-sm text-muted-foreground">Loading…</p>
          ) : threads.length === 0 ? (
            <div className="p-6 text-center" data-testid="threads-empty">
              <MessageSquare className="w-7 h-7 mx-auto text-muted-foreground" strokeWidth={1.5} />
              <p className="mt-2 text-sm text-muted-foreground">No conversations yet.</p>
            </div>
          ) : (
            <ul className="divide-y divide-border max-h-[540px] overflow-auto">
              {threads.map((t) => (
                <li key={t.other_id}>
                  <button
                    onClick={() => openThread(t.other_id)}
                    data-testid={`thread-item-${t.other_id}`}
                    className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-muted/60 transition-colors ${activeId === t.other_id ? "bg-muted" : ""}`}>
                    <span className="w-9 h-9 shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold">{initials(t.other_name)}</span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className="font-medium truncate">{t.other_name}</span>
                        <span className="text-[10px] text-muted-foreground shrink-0">{fmtTime(t.last_at)}</span>
                      </span>
                      <span className="text-xs text-muted-foreground truncate block">{t.last_body}</span>
                    </span>
                    {t.unread > 0 && (
                      <span className="ml-1 shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-white text-[11px] font-bold flex items-center justify-center" data-testid={`thread-unread-${t.other_id}`}>{t.unread}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* Conversation */}
        <section className={`flex flex-col ${activeId ? "flex" : "hidden md:flex"}`} data-testid="conversation-pane">
          {!activeId ? (
            <div className="flex-1 flex items-center justify-center text-center p-8">
              <div>
                <MessageSquare className="w-9 h-9 mx-auto text-muted-foreground" strokeWidth={1.5} />
                <p className="mt-3 text-muted-foreground">Select a conversation to start chatting.</p>
              </div>
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-border flex items-center gap-3">
                <Button variant="ghost" size="icon" className="md:hidden h-8 w-8" onClick={backToList} data-testid="conversation-back"><ArrowLeft className="w-4 h-4" /></Button>
                <span className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold">{initials(thread?.other?.name)}</span>
                <span className="font-medium" data-testid="conversation-title">{thread?.other?.name || "Conversation"}</span>
              </div>

              <div className="flex-1 overflow-auto p-4 space-y-3 bg-muted/30" data-testid="conversation-messages">
                {(thread?.messages || []).length === 0 ? (
                  <p className="text-center text-sm text-muted-foreground mt-8">No messages yet. Say hello 👋</p>
                ) : (
                  (thread?.messages || []).map((m) => {
                    const mine = m.sender_id === user?.id;
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
                        <div className={`max-w-[78%] rounded-sm px-3 py-2 text-sm ${mine ? "bg-primary text-white" : "bg-white border border-border"}`}>
                          {m.image_url && (
                            <a href={mediaUrl(m.image_url)} target="_blank" rel="noopener noreferrer" data-testid={`message-image-${m.id}`}>
                              <img src={mediaUrl(m.image_url)} alt="attachment" className="rounded-sm max-h-60 w-auto mb-1 border border-black/5" />
                            </a>
                          )}
                          {m.body && <p className="whitespace-pre-wrap break-words">{m.body}</p>}
                          <span className={`mt-1 flex items-center gap-1 justify-end text-[10px] ${mine ? "text-white/70" : "text-muted-foreground"}`}>
                            {fmtTime(m.created_at)}
                            {mine && (m.read_at
                              ? <CheckCheck className="w-3.5 h-3.5" data-testid={`read-${m.id}`} />
                              : <Check className="w-3.5 h-3.5" data-testid={`sent-${m.id}`} />)}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={bottomRef} />
              </div>

              <form onSubmit={send} className="p-3 border-t border-border">
                {attachment && (
                  <div className="mb-2 relative inline-block" data-testid="attachment-preview">
                    <img src={mediaUrl(attachment)} alt="to send" className="h-20 w-auto rounded-sm border border-border" />
                    <button type="button" onClick={() => setAttachment(null)} data-testid="attachment-remove"
                      className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-secondary text-white flex items-center justify-center"><X className="w-3 h-3" /></button>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input ref={fileRef} type="file" accept="image/*" onChange={pickFile} className="hidden" data-testid="attachment-input" />
                  <Button type="button" variant="ghost" size="icon" className="rounded-sm shrink-0" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="attachment-btn" aria-label="Attach photo">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                  </Button>
                  <Input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type a message…" className="rounded-sm" data-testid="message-input" />
                  <Button type="submit" disabled={sending || uploading || (!body.trim() && !attachment)} className="rounded-sm shrink-0" data-testid="message-send"><Send className="w-4 h-4" /></Button>
                </div>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
